import React, { useState } from "react";
import { motion } from "framer-motion";

const pricingTiers = [
  {
    name: "Free",
    truckCount: "Unlimited loads (first 2 weeks)",
    price: 0,
    currency: "AUD",
    description:
      "Unlimited loads for your first 2 weeks, then 10 loads free for the rest of the month — no strings attached.",
    features: [
      "Unlimited loads for first 2 weeks",
      "10 loads free for remaining 2 weeks",
      "Full access to Clutch Logistics platform",
      "Driver mobile app access",
      "Load management system",
      "Email support",
      "Basic reporting and analytics",
    ],
    ctaText: "Start Free",
    ctaLink: "https://go.clutchit.com.au/auth/signup",
  },
  {
    name: "Work Horse",
    truckCount: "1–5 trucks",
    price: 60,
    currency: "AUD",
    description:
      "Reliable solution for everyday logistics operations with smaller fleets.",
    features: [
      "Full access to Clutch Logistics platform",
      "Unlimited loads",
      "Driver mobile app access",
      "Load management system",
      "Email support",
      "Basic reporting and analytics",
    ],
    ctaText: "Get Started",
    ctaLink: "https://go.clutchit.com.au/auth/signup",
    popular: true,
  },
  {
    name: "Legend Status",
    truckCount: "6+ trucks",
    price: 40,
    currency: "AUD",
    description:
      "Enterprise-grade logistics platform built for high-volume transport operations.",
    features: [
      "Everything in Work Horse",
      "Unlimited loads",
      "Unlimited user accounts",
      "24/7 priority support",
      "Dedicated account manager",
    ],
    ctaText: "Get Started",
    ctaLink: "https://go.clutchit.com.au/auth/signup",
  },
];

function getTier(trucks) {
  if (trucks <= 5) return pricingTiers[1];
  return pricingTiers[2];
}

function getTotalPrice(trucks) {
  if (trucks <= 5) return trucks * 60;
  return 5 * 60 + (trucks - 5) * 40;
}

function getPerTruckPrice(trucks) {
  if (trucks <= 5) return "$60 per truck / month";
  return `$40 per truck / month (trucks 6–${trucks})`;
}

function getBreakdownLines(trucks) {
  const lines = [];
  if (trucks <= 5) {
    lines.push(`${trucks} truck${trucks > 1 ? "s" : ""} × $60 = $${trucks * 60}`);
  } else {
    lines.push(`5 trucks × $60 = $300`);
    lines.push(`${trucks - 5} truck${trucks - 5 > 1 ? "s" : ""} × $40 = $${(trucks - 5) * 40}`);
  }
  lines.push("---");
  lines.push(`Total: $${getTotalPrice(trucks)}`);
  return lines;
}

const includedFeatures = [
  {
    title: "Load Management",
    description: "Powerful load management system to optimise your operations and reduce empty runs.",
    icon: (
      <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
      </svg>
    ),
  },
  {
    title: "Driver Mobile App",
    description: "Native app for drivers with real-time job assignments, delivery updates and GPS routing.",
    icon: (
      <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 8.25h3" />
      </svg>
    ),
  },
  {
    title: "Real-Time Tracking",
    description: "Live GPS fleet monitoring and delivery status updates across your entire operation.",
    icon: (
      <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
      </svg>
    ),
  },
  {
    title: "Reporting & Analytics",
    description: "Built-in reporting to track performance, compliance, and fleet efficiency at a glance.",
    icon: (
      <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
      </svg>
    ),
  },
];

const faqs = [
  {
    q: "How is pricing calculated?",
    a: "Pricing is based on the number of active trucks in your fleet. The first 5 trucks are $60 each per month, and any additional trucks beyond 5 are $40 each.",
  },
  {
    q: "Can I change my plan later?",
    a: "No need to! We automatically charge you based on the number of active trucks in your fleet each month. You can also cancel anytime.",
  },
  {
    q: "Is there a setup fee?",
    a: "No setup fees, ever. You only pay the monthly subscription based on your fleet size.",
  },
  {
    q: "What support is included?",
    a: "All plans include email support. Legend Status plans include 24/7 priority support and a dedicated account manager.",
  },
];

const Pricing = () => {
  const [trucks, setTrucks] = useState(1);

  const urlParams = new URLSearchParams(window.location.search);
  const fromHero = urlParams.get("trial") === "free";

  React.useEffect(() => {
    if (fromHero) setTrucks(0);
  }, [fromHero]);

  const totalPrice = trucks === 0 ? 0 : getTotalPrice(trucks);
  const perTruckPrice = trucks === 0 ? "Free forever" : getPerTruckPrice(trucks);
  const breakdownLines = trucks === 0 ? ["Free plan — no charge"] : getBreakdownLines(trucks);

  return (
    <section className="section-padding bg-white relative overflow-hidden">
      <div className="absolute inset-0 bg-mesh-grid pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border-subtle to-transparent" />

      <div className="section-container relative">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="section-label">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
            </svg>
            Pricing
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4">
            Clutch Grows<br />
            <span className="text-gradient-blue">With You</span>
          </h1>
          <p className="text-secondary text-lg max-w-2xl mx-auto">
            Pricing automatically adjusts to your fleet size. Pay only for what you need, scale as you grow.
          </p>
        </motion.div>

        {/* Slider & Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex flex-col lg:flex-row gap-6 mb-12 max-w-4xl mx-auto"
        >
          {/* Slider Card */}
          <div className="rounded-2xl border border-border-subtle bg-white p-8 shadow-card flex-1">
            <h3 className="text-primary font-semibold text-base mb-6">How many trucks do you have?</h3>
            <div className="flex items-center gap-4 mb-8">
              <input
                id="truckSlider"
                type="range"
                min={0}
                max={50}
                value={trucks}
                onChange={(e) => setTrucks(Number(e.target.value))}
                className="flex-1 h-2 rounded-full appearance-none bg-surface-3 accent-accent cursor-pointer"
              />
              <span className="font-bold text-accent text-xl min-w-[2.5rem] text-center">
                {trucks === 0 ? "Any" : trucks}
              </span>
            </div>
            <div className="text-center">
              <p className="text-secondary text-sm mb-1">Estimated monthly cost</p>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-4xl font-bold text-primary">${totalPrice}</span>
                <span className="text-secondary text-sm">/month</span>
              </div>
              <div className="inline-flex items-center mt-3 px-3 py-1.5 rounded-full bg-accent/10">
                <span className="text-accent font-semibold text-sm">{perTruckPrice}</span>
              </div>
              <p className="text-secondary text-xs mt-3">Price updates as you move the slider</p>
            </div>
          </div>

          {/* Breakdown Card */}
          <div className="rounded-2xl border border-border-subtle bg-white p-8 shadow-card flex-1 flex flex-col">
            <h3 className="text-primary font-semibold text-base mb-6">Price Breakdown</h3>
            <div className="flex-1 space-y-3">
              {breakdownLines.map((line, idx) => {
                if (line.startsWith("---")) {
                  return <div key={idx} className="border-t border-border-subtle my-1" />;
                }
                if (line.startsWith("Total")) {
                  const totalMatch = line.match(/^(Total:)(\s*\$[0-9]+)/);
                  if (totalMatch) {
                    return (
                      <div key={idx} className="flex justify-between items-center pt-1">
                        <span className="text-primary font-bold">{totalMatch[1]}</span>
                        <span className="text-primary font-extrabold text-xl">{totalMatch[2]}</span>
                      </div>
                    );
                  }
                  return (
                    <div key={idx} className="font-bold text-lg text-center text-primary">{line}</div>
                  );
                }
                const match = line.match(/^(.*=)\s*(\$[0-9]+)/);
                if (match) {
                  return (
                    <div key={idx} className="flex justify-between items-center px-4 py-2.5 rounded-xl bg-accent/10">
                      <span className="text-accent font-semibold text-sm">{match[1]}</span>
                      <span className="text-accent font-bold text-sm">{match[2]}</span>
                    </div>
                  );
                }
                return (
                  <div key={idx} className="px-4 py-2.5 rounded-xl bg-accent/10 text-accent font-semibold text-sm">
                    {line}
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-16">
          {pricingTiers.map((tier, index) => {
            const isActive =
              (trucks === 0 && tier.name === "Free") ||
              (trucks > 0 && tier.name === getTier(trucks).name && tier.name !== "Free");

            return (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className={`relative rounded-2xl border bg-white p-6 shadow-card flex flex-col transition-all duration-300 ${
                  isActive
                    ? "border-accent shadow-glow-sm"
                    : "border-border-subtle hover:border-accent hover:shadow-glow-sm"
                }`}
              >
                {/* Badge */}
                {(isActive || tier.popular) && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap">
                    {isActive ? (
                      <span className="bg-accent text-white px-4 py-1.5 rounded-full text-xs font-semibold shadow-glow-sm">
                        Active Plan
                      </span>
                    ) : (
                      <span className="bg-surface-2 text-secondary border border-border-medium px-4 py-1.5 rounded-full text-xs font-semibold">
                        Most Popular
                      </span>
                    )}
                  </div>
                )}

                <div className="mb-5 mt-2">
                  <h3 className="text-xl font-bold text-primary mb-1">{tier.name}</h3>
                  <p className="text-secondary text-sm mb-4">{tier.truckCount}</p>
                  <div className="flex items-baseline gap-1 mb-3">
                    <span className="text-3xl font-bold text-accent">
                      {tier.price === 0 ? "$0" : `$${tier.price}`}
                    </span>
                    <span className="text-secondary text-sm">
                      {tier.price === 0 ? "/ month" : "/ truck / month"}
                    </span>
                  </div>
                  {tier.name === "Legend Status" && (
                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-accent/10 mb-3">
                      <span className="text-accent font-semibold text-xs">First 5 trucks $60, then $40 each</span>
                    </div>
                  )}
                  {tier.name === "Work Horse" && (
                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-accent/10 mb-3">
                      <span className="text-accent font-semibold text-xs">$60 per truck per month</span>
                    </div>
                  )}
                  <p className="text-secondary text-sm leading-relaxed">{tier.description}</p>
                </div>

                <ul className="space-y-2.5 mb-6 flex-grow">
                  {tier.features.map((feature, fi) => (
                    <li key={fi} className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-secondary text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={tier.ctaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary text-sm justify-center"
                >
                  {tier.ctaText}
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
                  </svg>
                </a>
              </motion.div>
            );
          })}
        </div>

        {/* What's Included */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-2xl border border-border-subtle bg-white p-8 shadow-card mb-16"
        >
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-primary mb-3">What's Included in Every Plan</h2>
            <p className="text-secondary max-w-2xl mx-auto">
              All pricing tiers include our complete suite of logistics management tools designed to streamline your operations.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {includedFeatures.map(({ title, description, icon }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="flex items-start gap-4"
              >
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                  {icon}
                </div>
                <div>
                  <h4 className="text-primary font-semibold mb-1">{title}</h4>
                  <p className="text-secondary text-sm leading-relaxed">{description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* FAQ */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primary">Frequently Asked Questions</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {faqs.map(({ q, a }, i) => (
              <motion.div
                key={q}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="rounded-2xl border border-border-subtle bg-white p-6 shadow-card hover:border-accent hover:shadow-glow-sm transition-all duration-300"
              >
                <h3 className="text-primary font-semibold mb-2">{q}</h3>
                <p className="text-secondary text-sm leading-relaxed">{a}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default Pricing;
